/**
 * deliberationCore — pure helpers over the Deliberation Record (Doc 21 §6.4).
 *
 * The record has four signal vectors; slice 1 of Brief K wires three of them
 * (rationale notes, session thread, override justifications) into the places
 * where decisions are made and into the session report. These helpers do the
 * grouping and the honest empty-state wording; the store does the I/O.
 */
import type { ItemAnnotation, OverrideJustification } from '../types/prioritization/deliberation';

export interface RationaleLine {
  kind: 'annotation' | 'override';
  /** The annotation's id (kind 'annotation' only) — what edit / retract act on. */
  annotationId?: string;
  itemId: string;
  authorId: string;
  text: string;
  createdAt: string;
  sessionId: string;
}

/** Everything recorded about one item, newest first, overrides and notes interleaved by time. */
export function rationaleForItem(
  itemId: string,
  annotations: readonly ItemAnnotation[],
  overrides: readonly OverrideJustification[],
): RationaleLine[] {
  const lines: RationaleLine[] = [];
  for (const a of annotations) {
    if (a.itemId !== itemId || a.retractedAt) continue;
    lines.push({ kind: 'annotation', annotationId: a.id, itemId, authorId: a.authorId, text: a.content, createdAt: a.createdAt, sessionId: a.sessionId });
  }
  for (const o of overrides) {
    if (o.itemId !== itemId) continue;
    lines.push({ kind: 'override', itemId, authorId: o.overriddenBy, text: o.reason, createdAt: o.createdAt, sessionId: o.sessionId });
  }
  return lines.sort((x, y) => (x.createdAt < y.createdAt ? 1 : x.createdAt > y.createdAt ? -1 : 0));
}

/** Per-item counts for a results table: how many recorded reasons sit behind each row. */
export function rationaleCountsByItem(
  annotations: readonly ItemAnnotation[],
  overrides: readonly OverrideJustification[],
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const a of annotations) {
    if (a.retractedAt) continue;
    counts.set(a.itemId, (counts.get(a.itemId) ?? 0) + 1);
  }
  for (const o of overrides) counts.set(o.itemId, (counts.get(o.itemId) ?? 0) + 1);
  return counts;
}

/** The one line the UI shows when nothing was recorded. Never synthesize a reason. */
export const NO_RATIONALE_RECORDED = 'No rationale was recorded for this item.';

/** Doc 21 §6.4: the override reason is the only mandatory signal input. */
export function overridesMissingReason<T extends { itemId: string; reason?: string | null }>(overrides: readonly T[]): T[] {
  return overrides.filter((o) => !o.reason || o.reason.trim().length === 0);
}

/** Trim + refuse empty; the DB CHECK does the same, this gives the user a sentence instead of a 23514. */
export function normalizeRationale(text: string): string | null {
  const t = text.trim();
  return t.length > 0 ? t : null;
}
